import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PosterPackButton } from "../components/PosterPackButton";
import PkhStudioTools from "./PkhStudioTools";
import PkhActivatePanel from "../components/pkh/PkhActivatePanel";
import {
  Plus, RefreshCw, Link2, Trash2, Send, Users, DollarSign, Clock,
  Image as ImageIcon, Download, Megaphone, Sparkles, ChevronDown, ChevronRight,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
const PUBLIC_URL = `${BACKEND}/powerkick-her`;
const ROSE = "#e11d48";

const KPI = ({ label, value, accent = "text-zinc-100", testid }) => (
  <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4" data-testid={testid}>
    <div className="text-xs text-zinc-400">{label}</div>
    <div className={`text-2xl font-bold ${accent}`}>{value}</div>
  </div>
);

const POSTER_FORMATS = [
  { key: "carre", label: "Post carré (1:1)" },
  { key: "story", label: "Story / Reel (9:16)" },
  { key: "imprimable", label: "Affiche imprimable" },
  { key: "porte", label: "Porte 120×60 cm" },
];

export default function PowerKickHerModule({ embedded = false } = {}) {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [campaigns, setCampaigns] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });
  const [pricing, setPricing] = useState(null);
  const [market, setMarket] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [assignSel, setAssignSel] = useState({});

  // Nouvelle session
  const [showNew, setShowNew] = useState(false);
  const [newS, setNewS] = useState({ title: "PowerKick Her — Bloc lancement", days: "Mardi & jeudi", time: "18h30 à 19h30", weeks: 12, start_date: "", capacity: 14, status: "preinscription", notes: "" });

  // Diffusion
  const [showBc, setShowBc] = useState(false);
  const [bc, setBc] = useState({ message: "", audience: "tous", send_sms: true, send_email: true });
  const [bcSending, setBcSending] = useState(false);

  // Ajout participante
  const [addFor, setAddFor] = useState(null);
  const [addForm, setAddForm] = useState({ member_name: "", email: "", phone: "", formule: "evaluation", note: "" });

  // Affiche
  const [posterFmt, setPosterFmt] = useState(null);
  const [posterUrl, setPosterUrl] = useState(null);

  const [posterProgress, setPosterProgress] = useState(0);

  const load = useCallback(async () => {
    const [d, p, c] = await Promise.allSettled([
      api.get("/powerkick-her/admin-dashboard"),
      api.get("/powerkick-her/prospects"),
      api.get("/powerkick-her/campaigns"),
    ]);
    if (d.status === "fulfilled") setDash(d.value.data);
    if (p.status === "fulfilled") setProspects(p.value.data.prospects || []);
    if (c.status === "fulfilled") setCampaigns(c.value.data);
    // On n'alerte que si le tableau de bord principal échoue (les autres sont secondaires)
    if (d.status === "rejected") toast.error("Erreur de chargement PowerKick Her");
  }, []);
  const loadBroadcasts = useCallback(async () => {
    try { const r = await api.get("/powerkick-her/broadcasts"); setBroadcasts(r.data.broadcasts || []); } catch { /* non bloquant */ }
  }, []);
  const loadExtras = useCallback(async () => {
    const [n, pr, m, s] = await Promise.allSettled([
      api.get("/powerkick-her/notifications"),
      api.get("/powerkick-her/pricing"),
      api.get("/powerkick-her/market-comparison"),
      api.get("/powerkick-her/survey/responses"),
    ]);
    if (n.status === "fulfilled") setNotifs(n.value.data);
    if (pr.status === "fulfilled") setPricing(pr.value.data);
    if (m.status === "fulfilled") setMarket(m.value.data);
    if (s.status === "fulfilled") setSurvey(s.value.data);
  }, []);

  useEffect(() => { load(); loadBroadcasts(); loadExtras(); }, [load, loadBroadcasts, loadExtras]);

  const markNotifsRead = async () => {
    try { await api.post("/powerkick-her/notifications/mark-read"); loadExtras(); } catch { /* */ }
  };
  const assignToSession = async (notifId) => {
    const sid = assignSel[notifId];
    if (!sid) return;
    try {
      await api.post(`/powerkick-her/notifications/${notifId}/assign`, { session_id: sid });
      toast.success("Inscrite ajoutée à la session ✓");
      loadExtras(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const savePricing = async () => {
    try { const r = await api.put("/powerkick-her/pricing", {
      base_price: Number(pricing.base_price), base_year: Number(pricing.base_year),
      annual_increase_pct: Number(pricing.annual_increase_pct),
      annual_increase_fixed: Number(pricing.annual_increase_fixed) });
      setPricing(r.data); toast.success("Tarification enregistrée ✓"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const copyAgentLink = async () => {
    const link = `${BACKEND.replace(/\/api$/, "")}/adhesion?pkh=sonia`;
    try { await navigator.clipboard.writeText(link); toast.success("Lien d'inscription de Sonia copié ✓"); }
    catch { toast.success(link); }
  };

  const seed = async () => { try { await api.post("/powerkick-her/seed-session"); toast.success("Session de lancement créée"); load(); } catch { toast.error("Erreur"); } };

  const createSession = async () => {
    try { await api.post("/powerkick-her/sessions", newS); toast.success("Session créée"); setShowNew(false); load(); }
    catch { toast.error("Erreur création session"); }
  };
  const deleteSession = async (id) => {
    if (!window.confirm("Supprimer cette session ?")) return;
    try { await api.delete(`/powerkick-her/sessions/${id}`); toast.success("Supprimée"); load(); } catch { toast.error("Erreur"); }
  };
  const notifyWaitlist = async (id) => {
    try { const r = await api.post(`/powerkick-her/sessions/${id}/notify-waitlist`); toast.success(`Relance envoyée (${r.data.sent || 0})`); }
    catch { toast.error("Erreur relance"); }
  };
  const addEnrollment = async () => {
    if (!addForm.member_name.trim()) { toast.error("Nom requis"); return; }
    try { await api.post(`/powerkick-her/sessions/${addFor}/enrollment`, addForm); toast.success("Participante ajoutée"); setAddFor(null); setAddForm({ member_name: "", email: "", phone: "", formule: "evaluation", note: "" }); load(); }
    catch { toast.error("Erreur ajout"); }
  };
  const paymentLink = async (sid, eid) => {
    try { const r = await api.post(`/powerkick-her/sessions/${sid}/enrollment/${eid}/payment-link`); await navigator.clipboard.writeText(r.data.checkout_url); toast.success("Lien de paiement copié"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const delEnrollment = async (sid, eid) => {
    try { await api.delete(`/powerkick-her/sessions/${sid}/enrollment/${eid}`); load(); } catch { toast.error("Erreur"); }
  };
  const delProspect = async (id) => {
    try { await api.delete(`/powerkick-her/prospects/${id}`); toast.success("Retiré"); load(); } catch { toast.error("Erreur (les évals ne se retirent pas ici)"); }
  };
  const offerSpot = async (id, name) => {
    if (!window.confirm(`Envoyer un courriel « place libérée » à ${name || "cette inscrite"} ?`)) return;
    try { const r = await api.post(`/powerkick-her/waitlist/${id}/offer-spot`); toast.success(`Courriel envoyé à ${r.data.email} ✓`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'envoi"); }
  };

  const sendBroadcast = async () => {
    if (!bc.message.trim()) { toast.error("Message vide"); return; }
    setBcSending(true);
    try {
      const r = await api.post("/powerkick-her/broadcast", bc);
      toast.success(`Envoyé : ${r.data.email_sent} courriels · ${r.data.sms_sent} SMS (${r.data.recipient_count} destinataires)`);
      setShowBc(false); setBc({ ...bc, message: "" }); loadBroadcasts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur diffusion"); }
    finally { setBcSending(false); }
  };

  const genPoster = async (fmt) => {
    setPosterFmt(fmt); setPosterUrl(null); setPosterProgress(0);
    const start = Date.now();
    const timer = setInterval(() => setPosterProgress(Math.min(95, Math.round(((Date.now() - start) / 70000) * 100))), 500);
    try {
      const r = await api.post("/poster/powerkick-her", { format: fmt, promo: false });
      let result = r.data.result;
      if (r.data.job_id) {
        const deadline = Date.now() + 240000;
        while (Date.now() < deadline) {
          await new Promise((res) => setTimeout(res, 3000));
          const s = await api.get(`/poster/job/${r.data.job_id}`);
          if (s.data.status === "done") { result = s.data.result || {}; break; }
          if (s.data.status === "error") throw new Error(s.data.error || "Échec");
        }
        if (!result) throw new Error("Délai dépassé");
      }
      setPosterProgress(100);
      const rel = result.url || result.full_url;
      setPosterUrl(rel?.startsWith("http") ? rel : `${BACKEND}${rel}`);
      toast.success("Affiche générée !");
    } catch (e) { toast.error(e?.message || "Échec de génération"); }
    finally { clearInterval(timer); setPosterFmt(null); }
  };


  if (!dash) return <div className="p-8 text-zinc-400">Chargement…</div>;

  return (
    <div className="p-5 md:p-8 space-y-6" data-testid="pkh-admin-module">
      {/* En-tête */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${ROSE}22`, color: ROSE }}>🥊</div>
          <div>
            <h1 className={embedded ? "text-xl font-bold text-white" : "text-2xl font-bold text-white"}>PowerKick Her{embedded ? <span className="text-zinc-500 text-sm font-normal"> · programme intégré aux Contrats Élèves</span> : null}</h1>
            <p className="text-sm text-zinc-400">{dash.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="pkh-broadcast-open" onClick={() => setShowBc(true)} className="px-3 py-2 rounded-lg text-black font-semibold text-sm flex items-center gap-2" style={{ background: ROSE }}>
            <Send size={15} /> Message à tous
          </button>
          <a data-testid="pkh-public-link" href={PUBLIC_URL} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg border border-white/15 text-zinc-200 text-sm flex items-center gap-2 hover:bg-white/5">
            <Link2 size={15} /> Lien public
          </a>
          <button data-testid="pkh-refresh" onClick={load} className="px-3 py-2 rounded-lg border border-white/15 text-zinc-200 text-sm flex items-center gap-2 hover:bg-white/5">
            <RefreshCw size={15} /> Actualiser
          </button>
        </div>
      </div>

      <PkhActivatePanel />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Préinscrites (sessions)" value={dash.enrolled_count} accent="text-white" testid="pkh-kpi-enrolled" />
        <KPI label="Prospects (évals)" value={dash.eval_count} accent="text-blue-400" testid="pkh-kpi-evals" />
        <KPI label="Liste d'attente" value={dash.waitlist_count} accent="text-amber-400" testid="pkh-kpi-waitlist" />
        <KPI label="Membres payantes" value={dash.members_count} accent="text-emerald-400" testid="pkh-kpi-members" />
        <KPI label="Revenu payé" value={`${dash.revenue_paid} $`} accent="text-emerald-400" testid="pkh-kpi-revenue" />
      </div>

      {/* 🔔 Alertes inscriptions (responsable du groupe — Sonia) */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5" data-testid="pkh-notifications">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles size={18} style={{ color: ROSE }} />
            <h2 className="font-semibold text-white">Nouvelles inscriptions à ajouter au groupe {notifs.unread > 0 && <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-rose-500 text-white" data-testid="pkh-notif-unread">{notifs.unread} non lues</span>}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyAgentLink} data-testid="pkh-copy-agent-link" className="px-3 py-1.5 rounded-lg border border-white/15 text-zinc-200 text-xs flex items-center gap-2 hover:bg-white/5"><Link2 size={14} /> Copier le lien d'inscription (Sonia)</button>
            {notifs.unread > 0 && <button onClick={markNotifsRead} data-testid="pkh-notif-mark-read" className="px-3 py-1.5 rounded-lg border border-white/15 text-zinc-200 text-xs hover:bg-white/5">Tout marquer lu</button>}
          </div>
        </div>
        {(!notifs.notifications || notifs.notifications.length === 0) ? (
          <p className="text-zinc-500 text-sm">Aucune inscription récente. Quand une nouvelle ou ancienne membre s'inscrit via le lien, l'alerte apparaît ici (et un courriel est envoyé à la responsable).</p>
        ) : (
          <ul className="space-y-2">
            {notifs.notifications.slice(0, 12).map((n) => (
              <li key={n.id} className={`flex items-start gap-3 p-2 rounded-lg ${n.read ? "opacity-70" : "bg-rose-500/5"}`} data-testid={`pkh-notif-${n.id}`}>
                <span className="mt-0.5">{n.assigned ? "✅" : (n.read ? "•" : "🔴")}</span>
                <div className="flex-1">
                  <div className="text-sm text-zinc-100">{n.title}</div>
                  <div className="text-xs text-zinc-400">{n.detail} · {(n.created_at || "").slice(0, 10)}</div>
                  {n.assigned ? (
                    <div className="text-xs text-emerald-400 mt-1" data-testid={`pkh-notif-assigned-${n.id}`}>Ajoutée à : {n.assigned_session_title || "session"}</div>
                  ) : (dash && dash.sessions && dash.sessions.length > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <select value={assignSel[n.id] || ""} onChange={(e) => setAssignSel({ ...assignSel, [n.id]: e.target.value })}
                        data-testid={`pkh-notif-session-${n.id}`} className="bg-zinc-800 border border-white/10 rounded px-2 py-1 text-xs text-white">
                        <option value="">Choisir une session…</option>
                        {dash.sessions.map((s) => <option key={s.id} value={s.id}>{s.title} ({s.enrolled}/{s.capacity})</option>)}
                      </select>
                      <button onClick={() => assignToSession(n.id)} data-testid={`pkh-notif-assign-${n.id}`}
                        className="px-3 py-1 rounded text-xs text-black font-semibold disabled:opacity-40" style={{ background: ROSE }} disabled={!assignSel[n.id]}>
                        Ajouter à la session
                      </button>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 💰 Tarification — hausse annuelle automatique */}
      {pricing && (
        <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5" data-testid="pkh-pricing">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign size={18} style={{ color: ROSE }} />
            <h2 className="font-semibold text-white">Tarification (hausse annuelle automatique · 1er janvier)</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 items-center">
            <div className="flex gap-6">
              <div><div className="text-xs text-zinc-400">Prix {pricing.current_year}</div><div className="text-2xl font-bold text-emerald-400" data-testid="pkh-price-current">{pricing.current_price} $</div></div>
              <div><div className="text-xs text-zinc-400">Prix {pricing.next_year} (auto)</div><div className="text-2xl font-bold text-amber-400" data-testid="pkh-price-next">{pricing.next_price} $</div></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-zinc-400">Prix de base ($)<input type="number" value={pricing.base_price} onChange={(e) => setPricing({ ...pricing, base_price: e.target.value })} data-testid="pkh-price-base" className="mt-1 w-full bg-zinc-800 border border-white/10 rounded px-2 py-1 text-white" /></label>
              <label className="text-xs text-zinc-400">Année de base<input type="number" value={pricing.base_year} onChange={(e) => setPricing({ ...pricing, base_year: e.target.value })} className="mt-1 w-full bg-zinc-800 border border-white/10 rounded px-2 py-1 text-white" /></label>
              <label className="text-xs text-zinc-400">Hausse annuelle (%)<input type="number" step="0.1" value={pricing.annual_increase_pct} onChange={(e) => setPricing({ ...pricing, annual_increase_pct: e.target.value })} data-testid="pkh-price-pct" className="mt-1 w-full bg-zinc-800 border border-white/10 rounded px-2 py-1 text-white" /></label>
              <label className="text-xs text-zinc-400">+ Montant fixe/an ($)<input type="number" value={pricing.annual_increase_fixed} onChange={(e) => setPricing({ ...pricing, annual_increase_fixed: e.target.value })} data-testid="pkh-price-fixed" className="mt-1 w-full bg-zinc-800 border border-white/10 rounded px-2 py-1 text-white" /></label>
            </div>
          </div>
          <button onClick={savePricing} data-testid="pkh-price-save" className="mt-3 px-4 py-2 rounded-lg text-black font-semibold text-sm" style={{ background: ROSE }}>Enregistrer la tarification</button>
          <p className="text-xs text-zinc-500 mt-2">Le prix s'applique automatiquement à toutes les inscriptions (publiques et membres). La hausse se déclenche seule chaque 1er janvier — aucune intervention requise.</p>
        </div>
      )}

      {/* 📊 Comparatif de marché — kickboxing femmes à Val-d'Or */}
      {market && (
        <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5" data-testid="pkh-market">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign size={18} style={{ color: ROSE }} />
            <h2 className="font-semibold text-white">Comparatif de marché — Val-d'Or (est-ce compétitif ?)</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-zinc-500 text-left text-xs uppercase"><th className="py-2 pr-4">Offre</th><th className="pr-4">Prix</th><th className="pr-4">≈ $/sem.</th><th>Note</th></tr></thead>
              <tbody>
                {(market.rows || []).map((r, i) => (
                  <tr key={i} className={`border-t border-white/5 ${r.is_us ? "text-rose-300 font-semibold" : "text-zinc-200"}`}>
                    <td className="py-2 pr-4">{r.name}<div className="text-xs text-zinc-500 font-normal">{r.offer}</div></td>
                    <td className="pr-4">{r.price} $</td><td className="pr-4">{r.per_week} $</td>
                    <td className="text-xs text-zinc-400">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {market.verdict && <p className="text-sm text-zinc-300 mt-3 border-l-2 border-rose-500 pl-3" data-testid="pkh-market-verdict">{market.verdict}</p>}
        </div>
      )}

      {/* 🌟 Sondage de fin de session */}
      {survey && (
        <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5" data-testid="pkh-survey">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} style={{ color: ROSE }} />
            <h2 className="font-semibold text-white">Satisfaction fin de session & témoignages</h2>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <KPI label="Réponses" value={survey.count} accent="text-white" testid="pkh-survey-count" />
            <KPI label="Note moyenne /5" value={survey.avg_rating} accent="text-amber-400" testid="pkh-survey-avg" />
            <KPI label="Recommandent" value={`${survey.recommend_pct}%`} accent="text-emerald-400" testid="pkh-survey-nps" />
          </div>
          {(survey.testimonials || []).length === 0 ? (
            <p className="text-zinc-500 text-sm">Les témoignages autorisés apparaîtront ici après la fin des sessions (envoi automatique du sondage à 12 semaines).</p>
          ) : (
            <ul className="space-y-2">
              {survey.testimonials.slice(0, 8).map((t, i) => (
                <li key={i} className="p-3 rounded-lg bg-white/5 text-sm text-zinc-200">"{t.testimonial}" <span className="text-zinc-500">— {t.name || "Participante"} ({t.rating}/5)</span></li>
              ))}
            </ul>
          )}
        </div>
      )}


      {/* 🎨 Affiche PKH + QR stats + calendrier de publications */}
      <PkhStudioTools />

      {/* Prospects combinés */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Users size={18} style={{ color: ROSE }} />
          <h2 className="font-semibold text-white">Prospects & liste d'attente ({prospects.length})</h2>
        </div>
        {prospects.length === 0 ? (
          <p className="text-zinc-500 text-sm">Aucun prospect pour l'instant. Les demandes de cours d'essai et les inscriptions à la liste d'attente apparaîtront ici.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pkh-prospects-table">
              <thead><tr className="text-zinc-500 text-left text-xs uppercase">
                <th className="py-2 pr-4">Nom</th><th className="pr-4">Type</th><th className="pr-4">Courriel</th><th className="pr-4">Tél.</th><th></th>
              </tr></thead>
              <tbody>
                {prospects.map((p) => (
                  <tr key={p.id} className="border-t border-white/5 text-zinc-200">
                    <td className="py-2 pr-4">{p.name || "—"}</td>
                    <td className="pr-4"><span className={`text-xs px-2 py-0.5 rounded ${p.type === "eval" ? "bg-blue-500/15 text-blue-300" : "bg-amber-500/15 text-amber-300"}`}>{p.type === "eval" ? "Cours d'essai" : "Liste d'attente"}</span>{p.spot_offered && <span className="ml-1 text-xs px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300" data-testid={`pkh-offered-${p.id}`}>Place offerte ✓</span>}</td>
                    <td className="pr-4">{p.email || "—"}</td>
                    <td className="pr-4">{p.phone || "—"}</td>
                    <td className="text-right whitespace-nowrap">
                      {p.type === "waitlist" && (
                        <>
                          <button onClick={() => offerSpot(p.id, p.name)} data-testid={`pkh-offer-spot-${p.id}`} className="text-emerald-400 hover:text-emerald-300 text-xs mr-3">Place libérée</button>
                          <button onClick={() => delProspect(p.id)} className="text-red-400 hover:text-red-300 text-xs">Retirer</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sessions */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2"><Clock size={18} style={{ color: ROSE }} /><h2 className="font-semibold text-white">Sessions de préinscription</h2></div>
          <div className="flex gap-2">
            {dash.sessions.length === 0 && <button data-testid="pkh-seed" onClick={seed} className="px-3 py-1.5 rounded-lg text-sm text-black font-semibold" style={{ background: ROSE }}>Créer la session de lancement</button>}
            <button data-testid="pkh-new-session" onClick={() => setShowNew(true)} className="px-3 py-1.5 rounded-lg text-sm border border-white/15 text-zinc-200 flex items-center gap-1 hover:bg-white/5"><Plus size={14} /> Nouvelle session</button>
          </div>
        </div>
        <div className="space-y-3">
          {dash.sessions.map((s) => (
            <div key={s.id} className="rounded-lg border border-white/10 bg-black/30">
              <div className="flex items-center justify-between p-4 flex-wrap gap-2">
                <button onClick={() => setExpanded({ ...expanded, [s.id]: !expanded[s.id] })} className="flex items-center gap-2 text-left">
                  {expanded[s.id] ? <ChevronDown size={16} className="text-zinc-500" /> : <ChevronRight size={16} className="text-zinc-500" />}
                  <div>
                    <div className="text-white font-medium">{s.title}{s.created_by_name && s.created_by_name !== "—" && <span className="ml-2 text-[10px] uppercase bg-white/10 text-zinc-400 px-1.5 py-0.5 rounded" data-testid={`pkh-created-by-${s.id}`}>créée par {s.created_by_name}</span>}</div>
                    <div className="text-xs text-zinc-400">{s.days} · {s.time} · {s.weeks} sem. · {s.enrolled}/{s.capacity} places {s.full && <span className="text-amber-400">(complet)</span>}</div>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded bg-white/10 text-zinc-300">{s.status}</span>
                  <button onClick={() => notifyWaitlist(s.id)} title="Relancer la liste d'attente" className="text-amber-400 hover:text-amber-300 text-xs flex items-center gap-1"><Megaphone size={13} /> Relancer</button>
                  <button onClick={() => { setAddFor(s.id); }} className="text-emerald-400 hover:text-emerald-300 text-xs flex items-center gap-1"><Plus size={13} /> Participante</button>
                  <button onClick={() => deleteSession(s.id)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
                </div>
              </div>
              {expanded[s.id] && (
                <div className="px-4 pb-4">
                  <div className="flex flex-wrap gap-2 mb-3" data-testid={`pkh-group-summary-${s.id}`}>
                    <span className="text-xs px-2 py-1 rounded bg-emerald-500/15 text-emerald-300">Actives (payées) : {(s.enrollments || []).filter((e) => e.payment_status === "paye").length}</span>
                    <span className="text-xs px-2 py-1 rounded bg-white/10 text-zinc-200">Inscrites : {s.enrolled}/{s.capacity}</span>
                    <span className={`text-xs px-2 py-1 rounded ${s.spots_left > 0 ? "bg-rose-500/15 text-rose-300" : "bg-amber-500/15 text-amber-300"}`}>Places restantes : {s.spots_left ?? Math.max(0, s.capacity - s.enrolled)}</span>
                  </div>
                  {(s.enrollments || []).length === 0 ? <p className="text-zinc-500 text-xs">Aucune inscrite.</p> : (
                    <table className="w-full text-xs">
                      <tbody>
                        {s.enrollments.map((e) => (
                          <tr key={e.eid} className="border-t border-white/5 text-zinc-300">
                            <td className="py-1.5 pr-2">{e.member_name}</td>
                            <td className="pr-2 text-zinc-500">{e.email || e.phone || "—"}</td>
                            <td className="pr-2">{e.amount > 0 ? `${e.amount} $` : "Gratuit"}</td>
                            <td className="pr-2"><span className={e.payment_status === "paye" ? "text-emerald-400" : "text-amber-400"}>{e.payment_status === "paye" ? "Payé" : "Impayé"}</span></td>
                            <td className="text-right">
                              {e.amount > 0 && e.payment_status !== "paye" && <button onClick={() => paymentLink(s.id, e.eid)} className="text-blue-400 hover:text-blue-300 mr-3">Lien paiement</button>}
                              <button onClick={() => delEnrollment(s.id, e.eid)} className="text-red-400 hover:text-red-300">Retirer</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Affiches + publicité */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5">
        <div className="flex items-center gap-2 mb-1"><ImageIcon size={18} style={{ color: ROSE }} /><h2 className="font-semibold text-white">Affiches promo & publicité</h2></div>
        <p className="text-zinc-500 text-sm mb-3">Génère une affiche PowerKick Her avec QR vers la page d'inscription régulière (sans rabais ni code promo), puis publie-la sur tes réseaux.</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {POSTER_FORMATS.map((f) => (
            <button key={f.key} data-testid={`pkh-poster-${f.key}`} onClick={() => genPoster(f.key)} disabled={!!posterFmt}
              className="px-3 py-2 rounded-lg border border-white/15 text-sm text-zinc-200 hover:bg-white/5 disabled:opacity-50">{f.label}</button>
          ))}
          <button data-testid="pkh-publish" onClick={() => navigate("/app/marketing-studio")} className="px-3 py-2 rounded-lg text-sm text-black font-semibold flex items-center gap-1" style={{ background: ROSE }}>
            <Sparkles size={14} /> Publier sur les réseaux
          </button>
          <PosterPackButton
            endpoint="/poster/powerkick-her/pack"
            getBody={() => ({ promo: false, style: "ia" })}
            filename="pack-powerkick-her.zip"
            label="Pack 4 formats (aperçu + ZIP)"
            testId="pkh-poster-pack"
            discipline="powerkick_her"
            disabled={!!posterFmt}
            className="px-3 py-2 rounded-lg border text-sm font-semibold flex items-center gap-1 disabled:opacity-50"
          />
        </div>
        {posterFmt && (
          <div className="max-w-md">
            <div className="text-xs text-zinc-400 mb-1">⏳ Génération… ({Math.round(posterProgress)} %)</div>
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${posterProgress}%`, background: ROSE }} /></div>
          </div>
        )}
        {posterUrl && (
          <div className="mt-3 flex items-start gap-4 flex-wrap">
            <img data-testid="pkh-poster-preview" src={posterUrl} alt="Affiche PowerKick Her" className="w-52 rounded-lg border border-white/10" />
            <a href={posterUrl} download className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm flex items-center gap-2"><Download size={15} /> Télécharger (PNG)</a>
          </div>
        )}
        <div className="mt-3 text-xs text-zinc-500 border-t border-white/5 pt-3">
          💡 Recommandations pub : publie la <b>Story 9:16</b> en réels Instagram/TikTok 3×/semaine, l'<b>affiche imprimable</b> chez les commerces partenaires (esthétique, café, gym), et la <b>Porte 120×60</b> sur la vitrine du dojo. Le QR mène à la préinscription — chaque scan est traçable.
        </div>
      </div>

      {/* Conversion des affiches (scan → inscription) */}
      {campaigns && campaigns.campaigns.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5" data-testid="pkh-campaigns">
          <h2 className="font-semibold text-white mb-1 flex items-center gap-2"><Sparkles size={16} style={{ color: ROSE }} /> Conversion des affiches</h2>
          <p className="text-zinc-500 text-xs mb-3">Quelle affiche convertit le mieux : scans du QR → préinscriptions. Total : {campaigns.totals.scans} scans · {campaigns.totals.signups} inscriptions · <b>{campaigns.totals.conversion_pct}%</b></p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-zinc-500 text-left text-xs uppercase"><th className="py-2 pr-4">Code / Affiche</th><th className="pr-4">Scans</th><th className="pr-4">Inscriptions</th><th>Conversion</th></tr></thead>
              <tbody>
                {campaigns.campaigns.map((c) => (
                  <tr key={c.code} className="border-t border-white/5 text-zinc-200">
                    <td className="py-2 pr-4 font-mono text-xs">{c.code}</td>
                    <td className="pr-4">{c.scans || 0}</td>
                    <td className="pr-4">{c.signups || 0}</td>
                    <td><span className={c.conversion_pct >= 20 ? "text-emerald-400" : c.conversion_pct > 0 ? "text-amber-400" : "text-zinc-500"}>{c.conversion_pct}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Historique diffusions */}
      {broadcasts.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5">
          <h2 className="font-semibold text-white mb-2 text-sm">Historique des diffusions</h2>
          <div className="space-y-1">
            {broadcasts.map((b) => (
              <div key={b.id} className="text-xs text-zinc-400 border-t border-white/5 py-1.5">
                <span className="text-zinc-500">{(b.created_at || "").slice(0, 16).replace("T", " ")}</span> · {b.audience} · {b.email_sent} courriels · {b.sms_sent} SMS — <span className="text-zinc-300">{(b.message || "").slice(0, 80)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MODALE nouvelle session */}
      {showNew && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="pkh-new-session-modal">
            <h3 className="text-lg font-bold text-white mb-4">Nouvelle session</h3>
            <div className="space-y-3 text-sm">
              <input value={newS.title} onChange={(e) => setNewS({ ...newS, title: e.target.value })} placeholder="Titre" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white" />
              <div className="grid grid-cols-2 gap-2">
                <input value={newS.days} onChange={(e) => setNewS({ ...newS, days: e.target.value })} placeholder="Jours" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white" />
                <input value={newS.time} onChange={(e) => setNewS({ ...newS, time: e.target.value })} placeholder="Heure" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input type="number" value={newS.weeks} onChange={(e) => setNewS({ ...newS, weeks: +e.target.value })} placeholder="Semaines" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white" />
                <input type="number" value={newS.capacity} onChange={(e) => setNewS({ ...newS, capacity: +e.target.value })} placeholder="Places" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white" />
                <input type="date" value={newS.start_date} onChange={(e) => setNewS({ ...newS, start_date: e.target.value })} className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button data-testid="pkh-create-session" onClick={createSession} className="flex-1 text-black font-bold py-2.5 rounded-lg" style={{ background: ROSE }}>Créer</button>
              <button onClick={() => setShowNew(false)} className="px-4 py-2.5 rounded-lg border border-zinc-700 text-zinc-300">Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE ajout participante */}
      {addFor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setAddFor(null)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="pkh-add-enrollment-modal">
            <h3 className="text-lg font-bold text-white mb-4">Ajouter une participante</h3>
            <div className="space-y-3 text-sm">
              <input value={addForm.member_name} onChange={(e) => setAddForm({ ...addForm, member_name: e.target.value })} placeholder="Nom complet" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white" data-testid="pkh-add-name" />
              <input value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="Courriel" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white" />
              <input value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} placeholder="Téléphone" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white" />
              <select value={addForm.formule} onChange={(e) => setAddForm({ ...addForm, formule: e.target.value })} className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white">
                <option value="evaluation">Cours d'essai — 19,98 $</option>
                <option value="lancement">Session (12 sem.) — 480 $</option>
                <option value="reguliere">Session de groupe (12 sem.) · 480 $</option>
              </select>
            </div>
            <div className="flex gap-2 mt-5">
              <button data-testid="pkh-add-submit" onClick={addEnrollment} className="flex-1 text-black font-bold py-2.5 rounded-lg" style={{ background: ROSE }}>Ajouter</button>
              <button onClick={() => setAddFor(null)} className="px-4 py-2.5 rounded-lg border border-zinc-700 text-zinc-300">Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE diffusion */}
      {showBc && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowBc(false)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="pkh-broadcast-modal">
            <h3 className="text-lg font-bold text-white mb-1">Message à tous</h3>
            <p className="text-zinc-500 text-xs mb-4">Envoie un message par courriel et/ou SMS.</p>
            <select value={bc.audience} onChange={(e) => setBc({ ...bc, audience: e.target.value })} className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mb-3">
              <option value="tous">Tout le monde (membres + prospects)</option>
              <option value="membres">Membres payantes seulement</option>
              <option value="prospects">Prospects / liste d'attente seulement</option>
            </select>
            <textarea value={bc.message} onChange={(e) => setBc({ ...bc, message: e.target.value })} rows={4} placeholder="Ton message…" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mb-3" data-testid="pkh-broadcast-message" />
            <div className="flex gap-4 mb-4 text-sm text-zinc-300">
              <label className="flex items-center gap-2"><input type="checkbox" checked={bc.send_email} onChange={(e) => setBc({ ...bc, send_email: e.target.checked })} /> Courriel</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={bc.send_sms} onChange={(e) => setBc({ ...bc, send_sms: e.target.checked })} /> SMS</label>
            </div>
            <div className="flex gap-2">
              <button data-testid="pkh-broadcast-send" onClick={sendBroadcast} disabled={bcSending} className="flex-1 text-black font-bold py-2.5 rounded-lg disabled:opacity-50" style={{ background: ROSE }}>{bcSending ? "Envoi…" : "Envoyer"}</button>
              <button onClick={() => setShowBc(false)} className="px-4 py-2.5 rounded-lg border border-zinc-700 text-zinc-300">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
